/**
 * The toast store. A module-level store rather than a context so any mutation handler can
 * announce a result without threading a provider through every screen; `<Toaster />` is the
 * single subscriber that renders it.
 *
 * Tone maps onto the callout family from the token file — no colour decision is made here.
 */

export type ToastTone = 'ok' | 'warn' | 'neutral';

export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
}

const DISMISS_AFTER_MS = 3600;

let current: Toast | null = null;
let sequence = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function emit(next: Toast | null): void {
  current = next;
  for (const listener of listeners) listener();
}

export function subscribeToToast(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getToast(): Toast | null {
  return current;
}

export function showToast(message: string, tone: ToastTone = 'ok'): void {
  if (timer !== undefined) clearTimeout(timer);
  sequence += 1;
  emit({ id: sequence, message, tone });
  timer = setTimeout(() => {
    emit(null);
  }, DISMISS_AFTER_MS);
}

export function dismissToast(): void {
  if (timer !== undefined) clearTimeout(timer);
  emit(null);
}
